import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { Button, Card, Grid } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import OutboxIcon from '@mui/icons-material/Outbox'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import { message } from 'antd'

import Helmet from '../../../../../../components/Helmet'
import HeaderContent from '../../../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../../../components/ContentWrapper'
import useAxios, { axiosInstance } from '../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../types/pagination'
import LeadListCancelFilter, {
  IFilterMapped,
} from './components/LeadListCancelFilter'
import LeadListCancelTable from './components/LeadListCancelTable'
import PageUrls from '../../../../../../constants/page-urls'
import { SendLeadFormModal } from '../../components/SendLeadFormModal'
import { MoveLeadFormModal } from '../../components/MoveLeadFormModal'
import { ECanceledAtStatus, ILead } from '../../../../../../types/lead'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../../../constants/global'
import * as XLSX from 'xlsx'

const Menus: any[] = []

const LeadListCancelPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  const [selectedLeads, setSelectedLeads] = useState<ILead[]>([])
  const [isModalSendLead, setIsModalSendLead] = useState<boolean>(false)
  const [isModalMoveLead, setIsModalMoveLead] = useState<boolean>(false)
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: Number(queryStr.perPage ?? 15),
    search: queryStr.search?.length
      ? String(queryStr.search).toLowerCase()
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    const filter = { ...newFilter, page: 1 }
    setFilter(filter)
    setSearchFilter(filter)
    const newQuery = queryString.stringify({ ...filter })
    navigate(`${PageUrls.CRM.Lead.Cancel}?${newQuery}`, {
      replace: true,
    })
  }

  const handlePageChange = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.CRM.Lead.Cancel}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(searchFilter)
  }, [])

  const params = {
    ...searchFilter,
    canceledAtStatus: ECanceledAtStatus.CANCELED,
    expiredDateStatus:
      (searchFilter?.expiredDateStatus as any) == 'all'
        ? undefined
        : searchFilter.expiredDateStatus,
  }

  const leadsList = useAxios<PaginationResponseInterface<ILead>>({
    method: 'GET',
    url: '/lead',
    params,
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      let loop = true
      let page = 1,
        perPage = MAX_DONWLOAD_PAGE_SIZE
      const data: ILead[] = []
      while (loop) {
        try {
          const result = await axiosInstance.get('/lead', {
            params: {
              ...params,
              page,
              perPage,
              startDate: date.startOf('month').format('YYYY-MM-DD'),
              endDate: date.endOf('month').format('YYYY-MM-DD'),
            },
          })
          page++
          if (!result?.data?.records.length) {
            loop = false
            break
          }
          data.push(...result.data.records)
          if (result?.data?.count < perPage) {
            loop = false
            break
          }
        } catch (e) {
          message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
          break
        }
      }
      if (!data.length) {
        message.warning('ไม่พบข้อมูล')
        return
      }
      const rows = data?.map((lead: ILead) => {
        return {
          'ชื่อ-นามสกุล': lead?.customer?.name,
          ช่องทางการขาย: lead?.distribution?.name,
          ที่อยู่: `${lead?.customer?.address} ${lead?.customer?.subDistrict} ${lead?.customer?.district} ${lead?.customer?.province} ${lead?.customer?.zipcode}`,
          หมายเลขโทรศัพท์: lead?.customer?.phone,
          ผู้ดูแล: lead?.assignTo?.name,
          PIPELINE: lead?.pipelineName,
          สถานะ: lead?.activePipelineItemName,
          วันหมดอายุ: lead?.expiredAt
            ? dayjs(lead?.expiredAt).format('DD/MM/YYYY')
            : '-',
          สร้างเมื่อ: lead?.cancelAt
            ? dayjs(lead?.cancelAt).format('DD/MM/YYYY')
            : '-',
          แก้ไขเมื่อ: lead?.updatedAt
            ? dayjs(lead?.updatedAt).format('DD/MM/YYYY')
            : '-',
        }
      })
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `lead-cancelled.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [params],
  )

  const onUpdateLeadListSuccess = useCallback(async () => {
    try {
      setSelectedLeads([])
      await leadsList.mutate()
    } catch (error) {
      message.error('ไม่สามารถดึงข้อมูล Lead Pipeline ได้')
    }
  }, [leadsList])

  return (
    <ContentWrapper>
      <Helmet title="รายการติดตาม ที่ยกเลิก ทั้งหมด" />
      <SendLeadFormModal
        objectIds={selectedLeads.map((lead) => lead.objectId!)}
        isOpen={isModalSendLead}
        onClose={(visible: boolean) => setIsModalSendLead(visible)}
        onUpdateLeadListSuccess={onUpdateLeadListSuccess}
      />
      <MoveLeadFormModal
        objectIds={selectedLeads.map((lead) => lead.objectId!)}
        isOpen={isModalMoveLead}
        onClose={(visible: boolean) => setIsModalMoveLead(visible)}
        onUpdateLeadListSuccess={onUpdateLeadListSuccess}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายการติดตาม ที่ยกเลิก ทั้งหมด"
            routes={Menus}
            descriptions="รายการติดตาม ที่ยกเลิก ทั้งหมด"
          />
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <LeadListCancelFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
        />
        {selectedLeads.length > 0 && (
          <Grid container spacing={2} mb={2}>
            <Grid item>
              <Button
                variant="contained"
                size="medium"
                startIcon={<OutboxIcon />}
                color={'info'}
                onClick={() => setIsModalSendLead(true)}
              >
                ส่งต่อ Lead {`(${selectedLeads.length})`}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="medium"
                startIcon={<DriveFileMoveIcon />}
                color={'warning'}
                onClick={() => setIsModalMoveLead(true)}
              >
                ย้าย Pipeline {`(${selectedLeads.length})`}
              </Button>
            </Grid>
            {/* <Grid item>
                <Button variant="contained" size="medium" startIcon={<DeleteIcon />} color={'error'}
                  onClick={() => onHandleCancelLead(selectedLeads)}
                >
                  ยกเลิก การติดตาม{`(${selectedLeads.length})`}
                </Button>
              </Grid> */}
          </Grid>
        )}
        <LeadListCancelTable
          selectedData={selectedLeads}
          setSelectedData={setSelectedLeads}
          isLoading={false}
          data={leadsList.data?.records ?? []}
          onUpdatePipelineListSuccess={onUpdateLeadListSuccess}
          filter={filter}
          onSearch={handleSearch}
          handlePageChange={handlePageChange}
          total={leadsList.data?.count ?? 0}
          onMoveLead={(lead: ILead) => {
            setSelectedLeads([lead])
            setIsModalMoveLead(true)
          }}
          onSendLead={(lead: ILead) => {
            setSelectedLeads([lead])
            setIsModalSendLead(true)
          }}
        />
      </Card>
    </ContentWrapper>
  )
}

export default LeadListCancelPage
